import { useId } from 'react'
import { OrganSegmentationType } from 'types/exam/types'
import {
  IKnownOrganSegmentation,
  ORGANS,
  getOrganSegmentationRect
} from '../constants/organs'

interface IOrganOverlay {
  segmentations: IKnownOrganSegmentation[]
  selected: OrganSegmentationType[]
  opacity: number
}

/** Matriz que troca o RGB pela cor do órgão e multiplica o alpha do PNG (sempre vermelho) por `opacity`. */
const toColorMatrix = (hex: string, opacity: number) => {
  const [r, g, b] = [1, 3, 5].map(
    index => Number.parseInt(hex.slice(index, index + 2), 16) / 255
  )
  return `0 0 0 0 ${r} 0 0 0 0 ${g} 0 0 0 0 ${b} 0 0 0 ${opacity} 0`
}

/**
 * Destaques dos órgãos selecionados sobre a imagem original. Deve cobrir o mesmo box da imagem
 * renderizada com `object-fit: contain`: o `viewBox` no tamanho da original com
 * `xMidYMid meet` reproduz esse encaixe, então o overlay segue o zoom/pan do container.
 */
const OrganOverlay: React.FC<IOrganOverlay> = ({
  segmentations,
  selected,
  opacity
}) => {
  const filterIdPrefix = useId()
  const visible = segmentations.filter(segmentation =>
    selected.includes(segmentation.segmentation_type)
  )

  if (visible.length === 0) return null

  const { source_width, source_height } = visible[0]

  return (
    <svg
      data-testid='organ-overlay'
      aria-hidden
      viewBox={`0 0 ${source_width} ${source_height}`}
      preserveAspectRatio='xMidYMid meet'
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
      }}
    >
      {visible.map(segmentation => {
        const type = segmentation.segmentation_type
        const filterId = `${filterIdPrefix}-${type}`
        const { x, y, size } = getOrganSegmentationRect(segmentation)
        return (
          <g key={type} data-organ={type}>
            <filter id={filterId} colorInterpolationFilters='sRGB'>
              <feColorMatrix
                type='matrix'
                values={toColorMatrix(ORGANS[type].color, opacity)}
              />
            </filter>
            <image
              href={segmentation.image}
              x={x}
              y={y}
              width={size}
              height={size}
              preserveAspectRatio='none'
              filter={`url(#${filterId})`}
            />
          </g>
        )
      })}
    </svg>
  )
}

export default OrganOverlay
